import axios from 'axios';
import { GET, polusBaseUrl, POST } from '../../../utils/axios-interceptor';
import { getTimeStamp } from '../../../utils/dateUtils';
import { getUniqueRequestId, REQUEST_ID_HEADER } from '../../investor/service';

export interface UpdateManagementProfileCustomFieldsPayload {
    assesseeOrgId: string;
    managementProfileId: string;
    // Flat map of bare fields + dotted "customFields.<key>" entries — underwriting-engine
    // enforces an allowlist against this map's keys.
    updates: Record<string, unknown>;
}

// Routed through polus BEFE, not straight to aica. assesseeOrgId is still
// sent — aica validates it against the caller's PNO, which it now reads from the
// Cerberus-verified MDC value rather than a forgeable header (the caller can't fake which
// PNO it belongs to, but the assessee org id itself isn't derivable from the token — see
// CibilController.java, UpdateManagementProfileCustomFieldsRequest#getAssesseeOrgId).
// Same {requestPayload: {...}} envelope + polusBaseUrl as getManagementProfilesV2Data below.
export function updateManagementProfileCustomFieldsService(payload: UpdateManagementProfileCustomFieldsPayload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.UPDATE_MANAGEMENT_PROFILE_CUSTOM_FIELDS,
        method: POST,
        // getTimeStamp() alone is second-granularity and collides when two writes fire in the
        // same second (see the getUniqueRequestId comment in store/investor/service.ts for the
        // prod incident this caused) — reuse that helper rather than the plain timestamp.
        headers: { [REQUEST_ID_HEADER]: getUniqueRequestId() },
        data: {
            requestPayload: {
                ...payload,
            },
        },
    });
}

export function getCompanyOverviewData(payload) {
    return axios({
        // baseURL: 'https://api-dev.recur.club/aica/rest',
        url: API_ENDPOINTS.FETCH_COMPANY_OVERVIEW,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            assesseeOrgId: payload.assesseeOrgId,
            investorOrgId: payload.investorOrgId,
            partnerUserId: payload.partnerUserId,
        },
    });
}

export function getScorecardData(payload) {
    return axios({
        url: API_ENDPOINTS.FETCH_SCORECARD_DATA,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            assesseeOrgId: payload.assesseeOrgId,
        },
    });
}

export function getAlertsData(payload) {
    return axios({
        url: API_ENDPOINTS.FETCH_ALERTS_DATA,
        method: POST,
        data: {
            requestId: getTimeStamp(),
            assesseeOrgId: payload.assesseeOrgId,
        },
    });
}

export function getCompanyOverviewDataV2(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.FETCH_COMPANY_OVERVIEW_V2,
        method: GET,
        params: {
            orgId: payload.assesseeOrgId,
            requestId: getTimeStamp(),
        },
    });
}

export function getShareholdingPatternData(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_GRAPH_DATA.url,
        method: GET,
        params: API_ENDPOINTS.GET_GRAPH_DATA.params(payload),
    });
}

export function getManagementProfileUserData(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_MANAGEMENT_PROFILE_USER_DATA,
        method: POST,
        data: {
            requestPayload: {
                ...payload,
            },
        },
    });
}

export function getManagementProfilesV2Data(payload: { orgId: string; filters?: Record<string, any> | null }) {
    // `filters` rides as a JSON STRING, not an object: polus substitutes it into the
    // underwriting service definition's query param via StringSubstitutor, which would
    // otherwise stringify a Map as `{a=b}` rather than JSON. Omitted entirely when there is
    // nothing to filter, so the placeholder stays unresolved and polus drops the param —
    // that is what keeps the endpoint's existing callers on their current contract.
    const hasFilters = !!payload.filters && Object.keys(payload.filters).length > 0;
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_ALL_MANAGEMENT_PROFILES,
        method: POST,
        data: {
            requestPayload: {
                orgId: payload.orgId,
                requestId: getTimeStamp(),
                ...(hasFilters && { filters: JSON.stringify(payload.filters) }),
            },
        },
    });
}

export function fetchFundingHistoryDataService(payload) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.FETCH_FUNDING_HISTORY_DATA,
        method: POST,
        data: {
            requestPayload: {
                ...payload,
                requestId: getTimeStamp(),
            },
        },
    });
}

export function getNetworkEntities(payload: { assesseeOrgId: string }) {
    return axios({
        baseURL: polusBaseUrl,
        url: API_ENDPOINTS.GET_NETWORK_ENTITIES,
        method: GET,
        params: { assesseeOrgId: payload.assesseeOrgId },
    });
}

export function getLenderConnectionsData(payload: { orgId: string }) {
    return axios({
        url: API_ENDPOINTS.FETCH_LENDER_CONNECTIONS,
        method: GET,
        params: {
            orgId: payload.orgId,
            activeOnly: false,
        },
    });
}

const API_ENDPOINTS = {
    GET_NETWORK_ENTITIES: '/unifier/getNetworkEntities',
    FETCH_COMPANY_OVERVIEW: '/profile/assessee/overview/main',
    FETCH_COMPANY_OVERVIEW_V2: '/unifier/getCompanyOverview',
    FETCH_SCORECARD_DATA: '/profile/assessee/aicaScorecard',
    FETCH_ALERTS_DATA: '/profile/assessee/overview/alerts',
    GET_GRAPH_DATA: {
        url: `/insights/tab/data`,
        method: GET,
        params: (payload) => ({
            requestId: getTimeStamp(),
            // multiple graphs can be passed as comma separated string
            graphs: payload.graphNames?.join(','),
            tabName: payload.tabName,
            startDate: payload.startDate,
            endDate: payload.endDate,
            tenure: payload.tenure,
            reportId: payload.reportId,
            directorId: payload?.directorId,
        }),
    },
    GET_MANAGEMENT_PROFILE_USER_DATA: '/unifier/getCompanyOverviewDirectorDetails',
    GET_ALL_MANAGEMENT_PROFILES: '/unifier/getManagementProfiles',
    FETCH_FUNDING_HISTORY_DATA: '/unifier/getFundingHistoryData',
    FETCH_LENDER_CONNECTIONS: '/profile/lender-connections',
    UPDATE_MANAGEMENT_PROFILE_CUSTOM_FIELDS: '/unifier/updateManagementProfile',
};
